import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send, X, Briefcase, Clock, Flag, Sparkles, Zap, CreditCard, CheckCircle2 } from "lucide-react";

const CATS = {
  business: { label: "Potential business", color: "#5ef2b0", icon: Briefcase, blurb: "Leads, deals and money-making chances the agent spotted." },
  needs_reply: { label: "Left on read", color: "#f5b301", icon: Clock, blurb: "People waiting on a reply from you." },
  obligation: { label: "Your obligations", color: "#ff6b8b", icon: Flag, blurb: "Things you promised and still owe." },
};

const InsightCard = ({ it, cat, onReplied, onDismiss }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(it.suggested_reply || "");
  const [sending, setSending] = useState(false);
  const C = CATS[cat];
  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try { const r = await api.imessageReply(it.person_id, text.trim()); toast.success(r.message || "Sent"); onReplied?.(it.id); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setSending(false); }
  };
  return (
    <div data-testid={`agent-insight-${it.id}`} className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-tactical font-bold text-sm text-zinc-100">{it.name}</span>
            <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: `${C.color}1f`, color: C.color }}><C.icon size={9} /> {C.label}</span>
            {it.priority >= 4 && <span className="text-[9px] font-mono uppercase text-rose-300">urgent</span>}
          </div>
          <p className="text-[13px] text-zinc-300 mt-1">{it.summary}</p>
          {cat === "obligation" && it.obligation && (
            <p className="text-[12px] text-rose-200/90 mt-1">You promised: <strong>{it.obligation}</strong></p>
          )}
        </div>
        <button data-testid={`agent-dismiss-${it.id}`} onClick={() => onDismiss(it.id)} title="Dismiss" className="text-zinc-600 hover:text-rose-300 shrink-0"><X size={14} /></button>
      </div>
      {(cat === "business" || cat === "needs_reply") && (
        <div className="mt-2">
          {!open ? (
            <button data-testid={`agent-reply-${it.id}`} onClick={() => setOpen(true)} className="flex items-center gap-1 text-[11px] font-mono text-cyan-300 border border-cyan-400/30 rounded-lg px-2.5 py-1.5 hover:bg-cyan-500/15">
              <Sparkles size={12} /> Review suggested reply
            </button>
          ) : (
            <div className="anim-slidein">
              <div className="text-[9px] uppercase tracking-wider text-cyan-300/70 font-mono mb-1">Suggested reply — edit then send</div>
              <div className="flex items-end gap-2">
                <textarea data-testid={`agent-reply-input-${it.id}`} rows={2} value={text} maxLength={1000} onChange={(e) => setText(e.target.value)} className="flex-1 bg-[#0b0e14] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 resize-none" />
                <button data-testid={`agent-send-${it.id}`} onClick={send} disabled={sending} className="h-9 px-3 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-[#04070c] font-tactical font-bold uppercase text-xs flex items-center gap-1">{sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function MessageAgent({ onTopUp, onReplied }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanningRecent, setScanningRecent] = useState(false);
  const [confirm, setConfirm] = useState(null); // { range, contacts, cost, balance, label }
  const [deep, setDeep] = useState(null);
  const poll = useRef(null);

  const load = () => api.imessageInsights().then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  const loadDeep = () => api.imessageDeepStatus().then(setDeep).catch(() => {});

  useEffect(() => {
    load(); loadDeep();
    poll.current = setInterval(() => { loadDeep(); }, 4000);
    return () => clearInterval(poll.current);
  }, []);

  // When a running deep scan finishes, refresh insights.
  const prevStatus = useRef(null);
  useEffect(() => {
    if (prevStatus.current === "running" && deep?.status === "done") { load(); toast.success("Deep scan complete — results are in."); }
    prevStatus.current = deep?.status;
  }, [deep?.status]);

  const scanRecent = async () => {
    setScanningRecent(true);
    try { const r = await api.imessageAgentScan(); toast.success(`Scanned ${r.analyzed} recent conversation${r.analyzed === 1 ? "" : "s"}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Scan failed"); }
    finally { setScanningRecent(false); }
  };

  const askDeep = async (range) => {
    try { const est = await api.imessageDeepEstimate(range); setConfirm(est); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not estimate"); }
  };

  const runDeep = async () => {
    const range = confirm.range;
    setConfirm(null);
    try { const r = await api.imessageDeepScan(range); toast.success(r.message); loadDeep(); prevStatus.current = "running"; }
    catch (e) {
      if (e.response?.status === 402) { toast.error(e.response?.data?.detail || "Not enough credits"); onTopUp?.(); }
      else toast.error(e.response?.data?.detail || "Could not start deep scan");
    }
  };

  const dismiss = async (id) => { try { await api.imessageDismissInsight(id); } catch {} load(); };
  const onCardReplied = (id) => { onReplied?.(); load(); };

  const g = data?.groups || { business: [], needs_reply: [], obligation: [] };
  const total = (g.business?.length || 0) + (g.needs_reply?.length || 0) + (g.obligation?.length || 0);
  const running = deep?.status === "running";

  return (
    <div className="space-y-4" data-testid="message-agent">
      {/* Scan controls */}
      <div className="rounded-xl border border-cyan-400/20 bg-cyan-500/[0.04] p-4">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={15} className="text-cyan-300" />
          <h3 className="font-tactical font-bold uppercase tracking-wider text-cyan-200 text-sm">Autonomous message agent</h3>
        </div>
        <p className="text-[12px] text-zinc-400 mb-3">It reads your conversations and surfaces <strong className="text-emerald-300">business</strong>, who you <strong className="text-amber-300">left on read</strong>, and <strong className="text-rose-300">promises you owe</strong> — with a one-tap reply.</p>
        <div className="flex flex-wrap gap-2">
          <button data-testid="agent-scan-recent" onClick={scanRecent} disabled={scanningRecent} className="inline-flex items-center gap-1.5 text-xs font-tactical uppercase tracking-wider rounded-lg px-3 h-9 bg-white/5 border border-white/10 text-zinc-200 hover:border-cyan-400/40 disabled:opacity-50">
            {scanningRecent ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} className="text-emerald-300" />} Scan recent · free
          </button>
          <button data-testid="agent-deep-year" onClick={() => askDeep("year")} disabled={running} className="inline-flex items-center gap-1.5 text-xs font-tactical uppercase tracking-wider rounded-lg px-3 h-9 bg-white/5 border border-white/10 text-zinc-200 hover:border-cyan-400/40 disabled:opacity-50">
            <CreditCard size={13} className="text-cyan-300" /> Deep scan · 12 months
          </button>
          <button data-testid="agent-deep-all" onClick={() => askDeep("all")} disabled={running} className="inline-flex items-center gap-1.5 text-xs font-tactical uppercase tracking-wider rounded-lg px-3 h-9 bg-white/5 border border-white/10 text-zinc-200 hover:border-cyan-400/40 disabled:opacity-50">
            <CreditCard size={13} className="text-cyan-300" /> Deep scan · all-time
          </button>
        </div>
        <p className="text-[10px] text-zinc-500 mt-2">Recent (last 7 days) is free and runs automatically on your own AI key. Deep scans cost <strong>1 credit per conversation</strong> and use LifeOS's built-in AI — you'll see the exact cost before anything is charged.</p>
        {running && (
          <div data-testid="agent-deep-progress" className="mt-3">
            <div className="flex items-center justify-between text-[11px] font-mono text-cyan-200 mb-1">
              <span className="flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Deep scanning {deep.range === "all" ? "all-time" : "12 months"}…</span>
              <span>{deep.done}/{deep.total}</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-cyan-400 rounded-full transition-all duration-700" style={{ width: `${deep.total ? Math.round((deep.done / deep.total) * 100) : 0}%` }} /></div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-zinc-400 text-sm font-mono py-12 justify-center"><Loader2 size={16} className="animate-spin" /> Loading insights…</div>
      ) : total === 0 ? (
        <div className="text-center text-zinc-500 py-12 text-sm" data-testid="agent-empty">
          Nothing flagged yet. Run a scan above and the agent will surface what needs you.
          {data && !data.has_own_key && <p className="text-[11px] text-amber-300/80 mt-2">Tip: connect your OpenAI key in AI Boss to unlock the free automatic recent scans.</p>}
        </div>
      ) : (
        Object.keys(CATS).map((cat) => (g[cat]?.length > 0) && (
          <div key={cat} data-testid={`agent-group-${cat}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-tactical uppercase tracking-wider" style={{ color: CATS[cat].color }}>{(() => { const I = CATS[cat].icon; return <I size={13} />; })()} {CATS[cat].label}</span>
              <span className="text-[10px] font-mono text-zinc-600">{g[cat].length}</span>
            </div>
            <div className="space-y-2">
              {g[cat].map((it) => <InsightCard key={it.id} it={it} cat={cat} onReplied={onCardReplied} onDismiss={dismiss} />)}
            </div>
          </div>
        ))
      )}

      {/* Cost confirm modal */}
      {confirm && (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-black/70 backdrop-blur-sm p-4" data-testid="agent-confirm-modal" onClick={() => setConfirm(null)}>
          <div className="w-full max-w-sm rounded-2xl border border-cyan-400/25 bg-[#0a1220] p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-2"><CreditCard size={16} className="text-cyan-300" /><h4 className="font-tactical font-bold uppercase tracking-wider text-cyan-200 text-sm">Confirm deep scan</h4></div>
            <p className="text-[13px] text-zinc-300">This will analyze <strong className="text-cyan-200">{confirm.contacts.toLocaleString()}</strong> conversation{confirm.contacts === 1 ? "" : "s"} from {confirm.label}.</p>
            <div className="mt-3 rounded-lg bg-white/[0.03] border border-white/10 p-3 text-[13px] space-y-1">
              <div className="flex justify-between"><span className="text-zinc-400">Cost</span><span className="font-tactical font-bold text-zinc-100">{confirm.cost.toLocaleString()} credits</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Your balance</span><span className={`font-tactical font-bold ${confirm.balance >= confirm.cost ? "text-emerald-300" : "text-rose-300"}`}>{confirm.balance.toLocaleString()} credits</span></div>
            </div>
            {confirm.balance < confirm.cost ? (
              <div className="mt-3">
                <p className="text-[12px] text-rose-300 mb-2">You need {(confirm.cost - confirm.balance).toLocaleString()} more credits.</p>
                <button data-testid="agent-topup" onClick={() => { setConfirm(null); onTopUp?.(); }} className="w-full h-10 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-[#04070c] font-tactical font-bold uppercase text-xs">Top up credits</button>
              </div>
            ) : (
              <div className="mt-4 flex gap-2">
                <button onClick={() => setConfirm(null)} className="flex-1 h-10 rounded-lg bg-white/5 border border-white/10 text-zinc-300 text-xs font-tactical uppercase">Cancel</button>
                <button data-testid="agent-confirm-run" onClick={runDeep} className="flex-1 h-10 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-[#04070c] font-tactical font-bold uppercase text-xs flex items-center justify-center gap-1"><CheckCircle2 size={14} /> Run · {confirm.cost} credits</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
